/**
 * Mentions de la réforme de la facture électronique (décision 9 du 19/10/2026) et franchise en
 * base de TVA (décision 29). Une seule définition pour l'écran, le PDF, le Factur-X et
 * l'import Pennylane :
 *  - catégorie d'opération de chaque facture (livraison de biens, prestation de services ou les
 *    deux), « prestation de services » par défaut, figée à l'émission ; code du cadre de
 *    facturation (BT-23) des spécifications externes françaises ;
 *  - option de l'entreprise pour le paiement de la TVA d'après les débits (réglage, figé sur la
 *    facture à l'émission ; BT-8 « date de facture » au Factur-X) ;
 *  - adresse de livraison, bornée, préremplie par l'adresse du chantier (ShipTo, BG-13) ;
 *  - SIREN du client, exigé pour un client professionnel établi en France ;
 *  - franchise en base de TVA (art. 293 B du CGI) : réglage de l'entreprise, aucune TVA facturée,
 *    mention légale imprimée, catégorie E et motif VATEX-FR-FRANCHISE au Factur-X.
 */
import { fr } from './i18n/fr.js';

export const OPERATION_CATEGORIES = ['services', 'biens', 'mixte'] as const;
export type OperationCategory = (typeof OPERATION_CATEGORIES)[number];

/** Catégorie d'une facture qui n'en a pas choisi : la plupart des factures de MyEquip sont des prestations. */
export const OPERATION_CATEGORY_DEFAULT: OperationCategory = 'services';

export const OPERATION_CATEGORY_LABELS: Record<OperationCategory, string> = fr.factures.categories;

/** Cadre de facturation (BT-23) d'une facture ordinaire : B1 biens, S1 services, M1 mixte. */
export const OPERATION_CATEGORY_BT23: Record<OperationCategory, string> = {
  biens: 'B1',
  services: 'S1',
  mixte: 'M1',
};

export function isOperationCategory(value: unknown): value is OperationCategory {
  return typeof value === 'string' && (OPERATION_CATEGORIES as readonly string[]).includes(value);
}

/** Longueur maximale d'une adresse de livraison (quatre lignes d'adresse confortables). */
export const DELIVERY_ADDRESS_MAX = 500;

/** Mention obligatoire d'une entreprise en franchise en base de TVA. */
export const VAT_FRANCHISE_MENTION = 'TVA non applicable, art. 293 B du CGI';
/** Motif d'exonération du Factur-X (liste VATEX) pour la franchise en base. */
export const VAT_FRANCHISE_VATEX = 'VATEX-FR-FRANCHISE';
/** Mention de l'option pour le paiement de la TVA d'après les débits. */
export const VAT_ON_DEBITS_MENTION = 'Option pour le paiement de la TVA d’après les débits';

/** SIREN (9 chiffres) tiré d'un SIREN ou d'un SIRET saisi, espaces tolérés ; sinon rien. */
export function sirenOf(value: string | null | undefined): string | undefined {
  const digits = (value ?? '').replace(/\s/g, '');
  return /^\d{9}(?:\d{5})?$/.test(digits) ? digits.slice(0, 9) : undefined;
}

export interface EInvoiceMentionsInput {
  operationCategory?: OperationCategory | null | undefined;
  vatOnDebits?: boolean | null | undefined;
  vatFranchise?: boolean | null | undefined;
}

/**
 * Mentions imprimées d'une facture émise ou à émettre, dans l'ordre : franchise en base,
 * option sur les débits (jamais avec la franchise : aucune TVA n'est due), catégorie d'opération.
 */
export function einvoiceMentions(input: EInvoiceMentionsInput): string[] {
  const mentions: string[] = [];
  if (input.vatFranchise) mentions.push(VAT_FRANCHISE_MENTION);
  else if (input.vatOnDebits) mentions.push(VAT_ON_DEBITS_MENTION);
  if (input.operationCategory) {
    mentions.push(`Catégorie d’opération : ${OPERATION_CATEGORY_LABELS[input.operationCategory]}`);
  }
  return mentions;
}
